// @ts-ignore
import React from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";

const integrationStyles = css({
    "alignItems": "center",
    "display": "flex",
    "justifyContent": "center",
    "padding": "52px 0",
    "backgroundImage":
        "url(https://wac-cdn.atlassian.com/dam/jcr:16647810-ab3b-4f93-b9f9-968c357b61a0/jsm-integrations-left.svg?cdnVersion=1325), url(https://wac-cdn.atlassian.com/dam/jcr:11294c16-3b1a-45c1-abbe-fc9b41b76a55/jsm-integrations-right.svg?cdnVersion=1325), linear-gradient(180deg, #ffffff, #deebff)",
    "backgroundRepeat": "no-repeat, no-repeat, no-repeat",
    [media.above.lg]: {
        backgroundPosition: "left center, right center, 0 0",
        backgroundSize: "auto, auto, 100% 100%"
    },
    [media.only.md]: {
        backgroundPosition: "left top 50%, right top 50%, 0 0",
        backgroundSize: "25% auto, 25% auto, 100% 100%"
    },
    [media.only.sm]: {
        backgroundPosition: "left top 130px, right top 130px, 0 0",
        backgroundSize: "24% auto, 24% auto, 100% 100%"
    },
    "@media (min-width: 540px) and (max-width: 767.98px)": {
        backgroundImage:
            "url(https://wac-cdn.atlassian.com/dam/jcr:9f835c55-ca31-4c7c-bf12-36fe2fd3b47b/integrations-lower-320.svg?cdnVersion=1326), url(https://wac-cdn.atlassian.com/dam/jcr:d9f1ed94-b4a2-4208-ae36-05ee56169023/integrations-upper-320.svg?cdnVersion=1326), linear-gradient(180deg, #ffffff, #deebff)",
        backgroundPosition: "center bottom, center top, 0 0",
        backgroundRepeat: "no-repeat, no-repeat, no-repeat",
        backgroundSize: "320px, 320px, 100% 100%",
        padding: "96px 0"
    },
    "@media (min-width: 0) and (max-width: 539.98px)": {
        backgroundImage:
            "url(https://wac-cdn.atlassian.com/dam/jcr:d9f1ed94-b4a2-4208-ae36-05ee56169023/integrations-upper-320.svg?cdnVersion=1326), url(https://wac-cdn.atlassian.com/dam/jcr:9f835c55-ca31-4c7c-bf12-36fe2fd3b47b/integrations-lower-320.svg?cdnVersion=1326), linear-gradient(180deg, #ffffff, #deebff)",
        backgroundPosition: "center top, center bottom, 0 0",
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundSize: "70%, 65%, 100%, 100%",
        padding: "96px 0"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".integrations-content": {
        [media.above.sm]: {
            padding: "16px",
            width: "50%"
        },

        [media.below.sm]: {
            width: "100%",
            padding: "96px 16px"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".text-section": {
            "marginTop": "32px",
            [media.below.md]: {
                marginBlockStart: 0
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "h2, p, a": {
                webkitFontSmoothing: "antialiased",
                mozOsxFontSmoothing: "grayscale"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "h2": {
                color: "#253858",
                fontWeight: 500,
                lineHeight: 1.33333333,
                [media.below.sm]: {
                    fontSize: "1.5rem",
                    marginBlockEnd: "14px"
                },
                [media.only.sm]: {
                    marginBlockEnd: "14px"
                },
                [media.above.sm]: {
                    fontSize: "2.25rem"
                }
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".contentBlock-body": {
                color: "#091e42",
                fontWeight: "normal",
                lineHeight: 1.5,
                [media.above.md]: {
                    marginBlockEnd: "24px"
                },
                [media.only.md]: {
                    marginBlockEnd: "24px",
                    fontSize: "1rem"
                },
                [media.below.md]: {
                    marginBlockEnd: "10px"
                }
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".contentBlock-eyebrow": {
                color: "#42526e",
                fontWeight: 400,
                letterSpacing: "1px",
                lineHeight: 2,
                [media.above.md]: {
                    marginBlockEnd: "16px"
                },
                [media.below.md]: {
                    marginBlockEnd: "10px"
                }
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".contentBlock-linkFlex": {
                lineHeight: 1,
                [media.below.md]: {
                    marginBlockEnd: 0
                },
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                a: {
                    margin: "0px",
                    height: "unset",
                    fontWeight: 400
                }
            }
        }
    }
});

const IntegrationSection = () => {
    const intl = useIntl();

    return (
        <section css={integrationStyles}>
            <div className="integrations-content">
                <ContentBlock
                    eyebrowText={intl.formatMessage({
                        id: "JiraServiceManagementPageTemplate.integrationSection.eyebrow",
                        defaultMessage: "Integrations for every team"
                    })}
                    headerText={intl.formatMessage({
                        id: "JiraServiceManagementPageTemplate.integrationSection.header",
                        defaultMessage:
                            "Supercharge service management with apps and integrations"
                    })}
                    bodyText={
                        <FormattedMessage
                            id="JiraServiceManagementPageTemplate.integrationSection.body"
                            defaultMessage="Atlassian Marketplace offers over 1,000 trusted applications and integrations to ensure Jira Service Management can be customized to fit your use case."
                            values={DEFAULT_VALUES}
                        />
                    }
                    headerType="h2"
                    isCentered={true}
                    orientation="vertical"
                    ctaProps={{
                        primaryLinkProps: {
                            text: intl.formatMessage({
                                id: "JiraServiceManagementPageTemplate.integrationSection.cta",
                                defaultMessage: "Explore the Marketplace"
                            }),
                            href: "https://marketplace.atlassian.com/",
                            type: "primary",
                            dataEventProps: {
                                "data-product-key": "jiraServiceDesk",
                                "data-event-component": "linkButton",
                                "data-event-container": "integrationsSection",
                                "data-event": "clicked"
                            }
                        }
                    }}
                />
            </div>
        </section>
    );
};

export default IntegrationSection;
